import React from "react";
import PageHeader from "../../common/PageHeader";
import {
  Paid,
  DeleteOutline,
  SearchOutlined,
  FilterListOutlined,
  CalendarMonthOutlined,
  DownloadOutlined,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Card,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import moment from "moment";
import { GlobalContext } from "../../context/GlobalContext";

// ── gateway chip colors ───────────────────────────────────────────────────────
const GATEWAY_COLOR = {
  stripe: "info",
  paypal: "primary",
  razorpay: "secondary",
  paystack: "success",
  mercadopago: "warning",
  free_trial: "default",
};

const GATEWAY_LABEL = {
  stripe: "Stripe",
  paypal: "PayPal",
  razorpay: "Razorpay",
  paystack: "Paystack",
  mercadopago: "Mercado Pago",
  free_trial: "Free Trial",
};

const PERIOD_PRESETS = [
  { value: "all", label: "All time" },
  { value: "last7", label: "Last 7 days" },
  { value: "last30", label: "Last 30 days" },
  { value: "this_month", label: "This month" },
  { value: "last_month", label: "Last month" },
];

function toApiDate(value) {
  if (!value || !moment(value).isValid()) return "";
  return moment(value).format("YYYY-MM-DD");
}

function fmtPeriodDate(value) {
  if (!value || !moment(value).isValid()) return "";
  return moment(value).format("DD MMM YYYY");
}

function isPaidRevenue(order) {
  return order.status === "paid" && order.gateway !== "free_trial";
}

function orderAmount(order) {
  return parseFloat(order.amount || 0) || 0;
}

function escapeCsvCell(v) {
  return `"${String(v ?? "").replace(/"/g, '""')}"`;
}

function downloadCsv(rows, filename) {
  const headers = [
    "Order ID",
    "Date",
    "Time",
    "User",
    "Email",
    "Plan",
    "Credits",
    "Transaction Type",
    "Gateway",
    "Amount (NGN)",
    "Status",
  ];
  const lines = [
    headers.join(","),
    ...rows.map((o) => {
      const d = new Date(o.created_at);
      return [
        o.id,
        d.toLocaleDateString("en-CA"),
        d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" }),
        o.user_name,
        o.user_email,
        o.plan_title,
        o.plan_credits,
        o.transaction_type,
        GATEWAY_LABEL[o.gateway] || o.gateway,
        o.gateway === "free_trial" ? 0 : orderAmount(o).toFixed(2),
        o.status,
      ]
        .map(escapeCsvCell)
        .join(",");
    }),
  ];
  const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

const Orders = ({ lang }) => {
  const { hitAxios } = React.useContext(GlobalContext);
  const theme = useTheme();

  const [orders, setOrders] = React.useState([]);
  const [search, setSearch] = React.useState("");
  const [gatewayFilter, setGatewayFilter] = React.useState("all");
  const [typeFilter, setTypeFilter] = React.useState("all");
  const [dateFrom, setDateFrom] = React.useState(null);
  const [dateTo, setDateTo] = React.useState(null);
  const [periodPreset, setPeriodPreset] = React.useState("all");
  const [deleteId, setDeleteId] = React.useState(null);
  const [deleting, setDeleting] = React.useState(false);

  const fmtNaira = (amount) => {
    const n = Number(amount || 0);
    return new Intl.NumberFormat("en-NG", {
      style: "currency",
      currency: "NGN",
      maximumFractionDigits: 2,
    }).format(Number.isFinite(n) ? n : 0);
  };

  // ── fetch orders ──────────────────────────────────────────────────────────
  const fetchOrders = React.useCallback(async () => {
    const params = new URLSearchParams();
    const from = toApiDate(dateFrom);
    const to = toApiDate(dateTo);
    if (from) params.set("date_from", from);
    if (to) params.set("date_to", to);
    const q = params.toString();

    const res = await hitAxios({
      path: `/api/payment/orders${q ? `?${q}` : ""}`,
      admin: true,
      post: false,
    });
    if (res?.data?.success) setOrders(res.data.data || []);
  }, [hitAxios, dateFrom, dateTo]);

  React.useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const applyPeriodPreset = (preset) => {
    setPeriodPreset(preset);
    const today = moment().endOf("day");

    switch (preset) {
      case "last7":
        setDateFrom(moment().subtract(6, "days").startOf("day"));
        setDateTo(today);
        break;
      case "last30":
        setDateFrom(moment().subtract(29, "days").startOf("day"));
        setDateTo(today);
        break;
      case "this_month":
        setDateFrom(moment().startOf("month"));
        setDateTo(today);
        break;
      case "last_month":
        setDateFrom(moment().subtract(1, "month").startOf("month"));
        setDateTo(moment().subtract(1, "month").endOf("month"));
        break;
      default:
        setDateFrom(null);
        setDateTo(null);
        break;
    }
  };

  const handleDateFromChange = (value) => {
    setPeriodPreset("custom");
    setDateFrom(value);
    if (value && dateTo && moment(value).isAfter(dateTo, "day")) {
      setDateTo(value);
    }
  };

  const handleDateToChange = (value) => {
    setPeriodPreset("custom");
    setDateTo(value);
    if (value && dateFrom && moment(value).isBefore(dateFrom, "day")) {
      setDateFrom(value);
    }
  };

  const clearDateRange = () => {
    setPeriodPreset("all");
    setDateFrom(null);
    setDateTo(null);
  };

  // ── delete order ──────────────────────────────────────────────────────────
  const handleDelete = async () => {
    setDeleting(true);
    const res = await hitAxios({
      path: `/api/payment/orders/${deleteId}`,
      admin: true,
      post: false,
      method: "DELETE",
    });
    setDeleting(false);
    if (res?.data?.success) {
      setOrders((prev) => prev.filter((o) => o.id !== deleteId));
      setDeleteId(null);
    }
  };

  // ── filter ────────────────────────────────────────────────────────────────
  const filtered = orders.filter((o) => {
    const matchGateway = gatewayFilter === "all" || o.gateway === gatewayFilter;
    const matchType =
      typeFilter === "all" || o.transaction_type === typeFilter;
    const q = search.toLowerCase();
    const matchSearch =
      !q ||
      o.user_email?.toLowerCase().includes(q) ||
      o.user_name?.toLowerCase().includes(q) ||
      o.plan_title?.toLowerCase().includes(q) ||
      o.transaction_type?.toLowerCase().includes(q) ||
      String(o.id).includes(q);
    return matchGateway && matchType && matchSearch;
  });

  const periodLabel = React.useMemo(() => {
    const from = fmtPeriodDate(dateFrom);
    const to = fmtPeriodDate(dateTo);
    if (from && to) return `${from} → ${to}`;
    if (from) return `From ${from}`;
    if (to) return `Until ${to}`;
    return lang?.allTime || "All time";
  }, [dateFrom, dateTo, lang]);

  const transactionSummary = React.useMemo(() => {
    const map = new Map();
    for (const o of orders) {
      const key = o.transaction_type || "Other";
      const row = map.get(key) || { type: key, count: 0, revenue: 0, credits: 0 };
      row.count += 1;
      if (isPaidRevenue(o)) {
        row.revenue += orderAmount(o);
        row.credits += Number(o.plan_credits || 0);
      }
      map.set(key, row);
    }
    return Array.from(map.values()).sort((a, b) => b.revenue - a.revenue);
  }, [orders]);

  const gatewayReconciliation = React.useMemo(() => {
    const map = new Map();
    for (const o of orders) {
      const key = o.gateway || "unknown";
      const row = map.get(key) || {
        gateway: key,
        orders: 0,
        paid: 0,
        revenue: 0,
        pending: 0,
      };
      row.orders += 1;
      if (o.status === "paid") row.paid += 1;
      if (o.status === "pending") row.pending += 1;
      if (isPaidRevenue(o)) row.revenue += orderAmount(o);
      map.set(key, row);
    }
    return Array.from(map.values()).sort((a, b) => b.revenue - a.revenue);
  }, [orders]);

  const handleExportCsv = () => {
    if (!filtered.length) return;
    const stamp = new Date().toISOString().slice(0, 10);
    const range =
      dateFrom || dateTo
        ? `_${toApiDate(dateFrom) || "start"}_${toApiDate(dateTo) || "end"}`
        : "";
    downloadCsv(filtered, `beyundai-orders${range}_${stamp}.csv`);
  };

  // ── stats ─────────────────────────────────────────────────────────────────
  const totalRevenue = orders
    .filter(isPaidRevenue)
    .reduce((sum, o) => sum + orderAmount(o), 0);

  const creditTopUps = orders.filter(
    (o) => o.transaction_type === "Credit Top-up" && isPaidRevenue(o),
  );
  const topUpRevenue = creditTopUps.reduce((s, o) => s + orderAmount(o), 0);
  const topUpCredits = creditTopUps.reduce(
    (s, o) => s + Number(o.plan_credits || 0),
    0,
  );

  const stats = [
    {
      label: lang?.totalOrders || "Total Orders",
      value: orders.length,
      color: "primary",
    },
    {
      label: lang?.paidOrders || "Paid Orders",
      value: orders.filter((o) => o.status === "paid").length,
      color: "success",
    },
    {
      label: lang?.freeTrials || "Free Trials",
      value: orders.filter((o) => o.gateway === "free_trial").length,
      color: "default",
    },
    {
      label: lang?.totalRevenue || "Total Revenue",
      value: fmtNaira(totalRevenue),
      color: "info",
    },
    {
      label: lang?.creditTopUps || "Credit Top-ups",
      value: fmtNaira(topUpRevenue),
      hint: `${topUpCredits.toLocaleString()} credits`,
      color: "secondary",
    },
  ];

  return (
    <Box>
      <PageHeader
        title={lang?.orders || "Orders"}
        subtitle={
          lang?.ordersSubBookkeeping ||
          "Payment ledger — filter by period, reconcile gateways, export CSV"
        }
        icon={Paid}
        primaryAction={
          <Button
            variant="outlined"
            startIcon={<DownloadOutlined />}
            onClick={handleExportCsv}
            disabled={!filtered.length}
          >
            {lang?.exportCsv || "Export CSV"}
          </Button>
        }
      />

      <Typography variant="caption" color="text.secondary" display="block" mb={2}>
        {lang?.reportingPeriod || "Reporting period"}: <strong>{periodLabel}</strong>
        {filtered.length !== orders.length
          ? ` · Showing ${filtered.length} of ${orders.length} in range`
          : orders.length
            ? ` · ${orders.length} transaction${orders.length === 1 ? "" : "s"}`
            : ""}
      </Typography>

      {/* ── STATS ROW ── */}
      <Stack
        direction="row"
        spacing={2}
        flexWrap="wrap"
        useFlexGap
        sx={{ mb: 3 }}
      >
        {stats.map((s) => (
          <Card
            key={s.label}
            elevation={0}
            sx={{
              flex: "1 1 160px",
              p: 2.5,
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 2,
              minWidth: 140,
            }}
          >
            <Typography
              variant="caption"
              color="text.secondary"
              fontWeight={600}
            >
              {s.label}
            </Typography>
            <Typography variant="h5" fontWeight={800} mt={0.5}>
              {s.value}
            </Typography>
            {s.hint && (
              <Typography variant="caption" color="text.secondary" display="block" mt={0.25}>
                {s.hint}
              </Typography>
            )}
          </Card>
        ))}
      </Stack>

      {/* ── BOOKKEEPING SUMMARIES ── */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} lg={6}>
          <Card
            elevation={0}
            sx={{
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 2,
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                px: 2,
                py: 1.5,
                bgcolor: alpha(theme.palette.primary.main, 0.04),
                borderBottom: `1px solid ${theme.palette.divider}`,
              }}
            >
              <Typography variant="subtitle2" fontWeight={700}>
                {lang?.byTransactionType || "By transaction type"}
              </Typography>
            </Box>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Type</TableCell>
                  <TableCell align="right">Count</TableCell>
                  <TableCell align="right">Revenue</TableCell>
                  <TableCell align="right">Credits</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {transactionSummary.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                      <Typography variant="caption" color="text.secondary">
                        No data for this period
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  transactionSummary.map((row) => (
                    <TableRow key={row.type} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          {row.type}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">{row.count}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>
                        {fmtNaira(row.revenue)}
                      </TableCell>
                      <TableCell align="right">
                        {row.credits > 0 ? row.credits.toLocaleString() : "—"}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </Card>
        </Grid>

        <Grid item xs={12} lg={6}>
          <Card
            elevation={0}
            sx={{
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 2,
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                px: 2,
                py: 1.5,
                bgcolor: alpha(theme.palette.success.main, 0.06),
                borderBottom: `1px solid ${theme.palette.divider}`,
              }}
            >
              <Typography variant="subtitle2" fontWeight={700}>
                {lang?.gatewayReconciliation || "Gateway reconciliation"}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Paid revenue by payment provider for month-end close
              </Typography>
            </Box>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Gateway</TableCell>
                  <TableCell align="right">Orders</TableCell>
                  <TableCell align="right">Paid</TableCell>
                  <TableCell align="right">Pending</TableCell>
                  <TableCell align="right">Revenue</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {gatewayReconciliation.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                      <Typography variant="caption" color="text.secondary">
                        No data for this period
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  gatewayReconciliation.map((row) => (
                    <TableRow key={row.gateway} hover>
                      <TableCell>
                        <Chip
                          size="small"
                          label={GATEWAY_LABEL[row.gateway] || row.gateway}
                          color={GATEWAY_COLOR[row.gateway] || "default"}
                          sx={{ fontWeight: 700, fontSize: "0.7rem" }}
                        />
                      </TableCell>
                      <TableCell align="right">{row.orders}</TableCell>
                      <TableCell align="right">{row.paid}</TableCell>
                      <TableCell align="right">
                        {row.pending > 0 ? (
                          <Typography variant="body2" color="warning.main" fontWeight={600}>
                            {row.pending}
                          </Typography>
                        ) : (
                          "0"
                        )}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>
                        {fmtNaira(row.revenue)}
                      </TableCell>
                    </TableRow>
                  ))
                )}
                {gatewayReconciliation.length > 0 && (
                  <TableRow sx={{ bgcolor: alpha(theme.palette.divider, 0.08) }}>
                    <TableCell>
                      <Typography variant="body2" fontWeight={800}>
                        Total
                      </Typography>
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>
                      {gatewayReconciliation.reduce((s, r) => s + r.orders, 0)}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>
                      {gatewayReconciliation.reduce((s, r) => s + r.paid, 0)}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>
                      {gatewayReconciliation.reduce((s, r) => s + r.pending, 0)}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 800 }}>
                      {fmtNaira(totalRevenue)}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Card>
        </Grid>
      </Grid>

      {/* ── FILTERS ── */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1.5}
        alignItems={{ sm: "center" }}
        mb={2}
      >
        <TextField
          size="small"
          placeholder={lang?.searchOrders || "Search by user, plan, ID…"}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ flex: 1, maxWidth: { sm: 340 } }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchOutlined fontSize="small" />
              </InputAdornment>
            ),
          }}
        />

        <Stack direction="row" alignItems="center" spacing={1}>
          <FilterListOutlined fontSize="small" color="action" />
          <Select
            size="small"
            value={gatewayFilter}
            onChange={(e) => setGatewayFilter(e.target.value)}
            sx={{ minWidth: 150 }}
          >
            <MenuItem value="all">
              {lang?.allGateways || "All Gateways"}
            </MenuItem>
            {Object.entries(GATEWAY_LABEL).map(([key, label]) => (
              <MenuItem key={key} value={key}>
                {label}
              </MenuItem>
            ))}
          </Select>
        </Stack>

        <Select
          size="small"
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          sx={{ minWidth: 170 }}
          displayEmpty
        >
          <MenuItem value="all">
            {lang?.allTransactionTypes || "All transaction types"}
          </MenuItem>
          {transactionSummary.map((row) => (
            <MenuItem key={row.type} value={row.type}>
              {row.type}
            </MenuItem>
          ))}
        </Select>

        <LocalizationProvider dateAdapter={AdapterMoment}>
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            flexWrap="wrap"
            useFlexGap
          >
            <CalendarMonthOutlined fontSize="small" color="action" />
            <Select
              size="small"
              value={periodPreset}
              onChange={(e) => applyPeriodPreset(e.target.value)}
              sx={{ minWidth: 150 }}
              displayEmpty
            >
              {PERIOD_PRESETS.map((p) => (
                <MenuItem key={p.value} value={p.value}>
                  {lang?.[`period_${p.value}`] || p.label}
                </MenuItem>
              ))}
              {periodPreset === "custom" && (
                <MenuItem value="custom" disabled>
                  {lang?.customRange || "Custom range"}
                </MenuItem>
              )}
            </Select>
            <DatePicker
              label={lang?.fromDate || "From"}
              value={dateFrom}
              onChange={handleDateFromChange}
              maxDate={dateTo || undefined}
              format="DD MMM YYYY"
              slotProps={{
                textField: {
                  size: "small",
                  sx: { width: { xs: "100%", sm: 168 } },
                },
                field: { clearable: true },
              }}
            />
            <DatePicker
              label={lang?.toDate || "To"}
              value={dateTo}
              onChange={handleDateToChange}
              minDate={dateFrom || undefined}
              maxDate={moment()}
              format="DD MMM YYYY"
              slotProps={{
                textField: {
                  size: "small",
                  sx: { width: { xs: "100%", sm: 168 } },
                },
                field: { clearable: true },
              }}
            />
            <Button
              size="small"
              variant="text"
              onClick={clearDateRange}
              disabled={!dateFrom && !dateTo}
            >
              {lang?.clear || "Clear"}
            </Button>
          </Stack>
        </LocalizationProvider>

        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ ml: "auto" }}
        >
          {filtered.length} {lang?.results || "results"}
        </Typography>
      </Stack>

      {/* ── TABLE ── */}
      <Card
        elevation={0}
        sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 2 }}
      >
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow
                sx={{
                  bgcolor: alpha(theme.palette.primary.main, 0.04),
                  "& th": {
                    fontWeight: 700,
                    fontSize: "0.75rem",
                    letterSpacing: "0.05em",
                    textTransform: "uppercase",
                    color: "text.secondary",
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    py: 1.5,
                    whiteSpace: "nowrap",
                  },
                }}
              >
                <TableCell>{lang?.orderId || "Order ID"}</TableCell>
                <TableCell>{lang?.user || "User"}</TableCell>
                <TableCell>{lang?.plan || "Plan"}</TableCell>
                <TableCell>{lang?.transactionType || "Transaction"}</TableCell>
                <TableCell>{lang?.gateway || "Gateway"}</TableCell>
                <TableCell>{lang?.amount || "Amount"}</TableCell>
                <TableCell>{lang?.status || "Status"}</TableCell>
                <TableCell>{lang?.date || "Date"}</TableCell>
                <TableCell align="center">
                  {lang?.actions || "Actions"}
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 6 }}>
                    <Typography color="text.secondary" variant="body2">
                      {lang?.noOrders || "No orders found"}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filtered.map((order) => {
                  const meta = (() => {
                    try {
                      return JSON.parse(order.meta || "{}");
                    } catch {
                      return {};
                    }
                  })();

                  return (
                    <TableRow
                      key={order.id}
                      hover
                      sx={{
                        "& td": {
                          py: 1.25,
                          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                          fontSize: "0.8125rem",
                        },
                      }}
                    >
                      {/* ID */}
                      <TableCell>
                        <Typography
                          variant="caption"
                          fontWeight={700}
                          color="text.secondary"
                        >
                          #{order.id}
                        </Typography>
                      </TableCell>

                      {/* User */}
                      <TableCell>
                        <Stack spacing={0.25}>
                          <Typography variant="body2" fontWeight={600} noWrap>
                            {order.user_name || "—"}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            noWrap
                          >
                            {order.user_email || "—"}
                          </Typography>
                        </Stack>
                      </TableCell>

                      {/* Plan */}
                      <TableCell>
                        <Stack spacing={0.25}>
                          <Typography variant="body2" fontWeight={600} noWrap>
                            {order.plan_title || "—"}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {order.plan_credits
                              ? `${Number(order.plan_credits).toLocaleString()} credits · ${order.expiry_days}d`
                              : "—"}
                          </Typography>
                        </Stack>
                      </TableCell>

                      {/* Transaction Type */}
                      <TableCell>
                        <Typography variant="body2" fontWeight={600} noWrap>
                          {order.transaction_type || "—"}
                        </Typography>
                      </TableCell>

                      {/* Gateway */}
                      <TableCell>
                        <Chip
                          label={GATEWAY_LABEL[order.gateway] || order.gateway}
                          color={GATEWAY_COLOR[order.gateway] || "default"}
                          size="small"
                          sx={{ fontWeight: 700, fontSize: "0.7rem" }}
                        />
                      </TableCell>

                      {/* Amount */}
                      <TableCell>
                        <Typography variant="body2" fontWeight={700}>
                          {order.gateway === "free_trial" ? (
                            <Chip
                              label={lang?.free || "Free"}
                              size="small"
                              variant="outlined"
                            />
                          ) : (
                            fmtNaira(order.amount)
                          )}
                        </Typography>
                      </TableCell>

                      {/* Status */}
                      <TableCell>
                        <Chip
                          label={order.status}
                          color={
                            order.status === "paid"
                              ? "success"
                              : order.status === "pending"
                                ? "warning"
                                : "error"
                          }
                          size="small"
                          variant="outlined"
                          sx={{
                            fontWeight: 700,
                            fontSize: "0.7rem",
                            textTransform: "capitalize",
                          }}
                        />
                      </TableCell>

                      {/* Date */}
                      <TableCell>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          noWrap
                        >
                          {new Date(order.created_at).toLocaleDateString(
                            "en-US",
                            {
                              year: "numeric",
                              month: "short",
                              day: "numeric",
                            },
                          )}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.disabled"
                          display="block"
                          noWrap
                        >
                          {new Date(order.created_at).toLocaleTimeString(
                            "en-US",
                            {
                              hour: "2-digit",
                              minute: "2-digit",
                            },
                          )}
                        </Typography>
                      </TableCell>

                      {/* Actions */}
                      <TableCell align="center">
                        <Tooltip title={lang?.deleteOrder || "Delete Order"}>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => setDeleteId(order.id)}
                          >
                            <DeleteOutline fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* ── DELETE CONFIRM DIALOG ── */}
      <Dialog
        open={!!deleteId}
        onClose={() => !deleting && setDeleteId(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          {lang?.confirmDelete || "Delete Order?"}
        </DialogTitle>
        <Divider />
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            {lang?.deleteOrderWarning ||
              "This will permanently delete the order record. The user's plan will not be affected."}
          </Typography>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ px: 3, py: 2 }}>
          <LoadingButton
            variant="outlined"
            color="inherit"
            onClick={() => setDeleteId(null)}
            disabled={deleting}
          >
            {lang?.cancel || "Cancel"}
          </LoadingButton>
          <LoadingButton
            variant="contained"
            color="error"
            loading={deleting}
            onClick={handleDelete}
          >
            {lang?.delete || "Delete"}
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Orders;
